import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Clock,
  Trash2,
  Play,
  Search,
  Calendar,
  ChevronRight,
  Mic,
  FileText,
  Image as ImageIcon,
  Calculator,
  Loader2,
  RefreshCw,
  X,
  User,
  Bot,
  Download
} from 'lucide-react';
import { AppShell, GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { sessionApi, imageApi, Session, Message, SessionDetail } from '@/lib/api';

// Extended session with UI properties
interface SessionUI {
  id: string;
  type: 'voice' | 'document' | 'image' | 'calculator';
  messageCount: number;
  title: string | null;
  preview: string;
  summary: string | null;
  started_at: string;
  ended_at?: string | null;
  duration_seconds?: number | null;
  user_level?: string | null;
  // For image sessions
  imageData?: string;
  mimeType?: string;
}

const typeIcons = {
  voice: Mic,
  document: FileText,
  image: ImageIcon,
  calculator: Calculator,
};

const typeColors = {
  voice: 'text-ocean-cyan bg-ocean-cyan/20',
  document: 'text-ocean-purple bg-ocean-purple/20',
  image: 'text-ocean-pink bg-ocean-pink/20',
  calculator: 'text-ocean-blue bg-ocean-blue/20',
};

// Determine session type from summary/content
const getSessionType = (session: Session): 'voice' | 'document' | 'image' | 'calculator' => {
  const summary = session.summary?.toLowerCase() || '';
  if (summary.includes('document') || summary.includes('charter') || summary.includes('contract')) {
    return 'document';
  }
  if (summary.includes('image') || summary.includes('generated') || summary.includes('visualization')) {
    return 'image';
  }
  if (summary.includes('calculation') || summary.includes('laytime') || summary.includes('demurrage')) {
    return 'calculator';
  }
  return 'voice'; // Default to voice
};

// Format duration from seconds
const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Sessions Page - View and manage conversation history
 */
export function Sessions() {
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState<SessionUI[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedSession, setSelectedSession] = useState<SessionUI | null>(null);

  // Session detail modal state (BUG-004 fix)
  const [sessionDetail, setSessionDetail] = useState<SessionDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Fetch session detail when a session is selected (BUG-004 fix)
  useEffect(() => {
    if (selectedSession && !selectedSession.id.startsWith('img-')) {
      const fetchDetail = async () => {
        setIsLoadingDetail(true);
        setDetailError(null);
        try {
          const { data } = await sessionApi.get(selectedSession.id);
          setSessionDetail(data);
        } catch (err: any) {
          console.error('Failed to fetch session detail:', err);
          setDetailError(err.message || 'Failed to load conversation');
        } finally {
          setIsLoadingDetail(false);
        }
      };
      fetchDetail();
    } else {
      setSessionDetail(null);
    }
  }, [selectedSession]);

  // Close modal handler
  const closeModal = () => {
    setSelectedSession(null);
    setSessionDetail(null);
    setDetailError(null);
  };

  // Handle auto-selection from URL param (BUG-014 fix)
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && sessions.length > 0 && !selectedSession) {
      const session = sessions.find(s => s.id === selectedId);
      if (session) {
        setSelectedSession(session);
      }
    }
  }, [searchParams, sessions, selectedSession]);

  // Fetch sessions and images from API
  const fetchSessions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch both conversation sessions and generated images in parallel
      const [sessionsResponse, imagesResponse] = await Promise.all([
        sessionApi.list(0, 50).catch(() => ({ data: [] })),
        imageApi.history(0, 50).catch(() => ({ data: [] })),
      ]);
      
      // Transform conversation sessions to UI format
      const conversationSessions: SessionUI[] = sessionsResponse.data.map(session => ({
        id: session.id,
        type: getSessionType(session),
        messageCount: 0,
        title: session.title,
        preview: session.summary || 'No summary available',
        summary: session.summary,
        started_at: session.started_at,
        ended_at: session.ended_at,
        duration_seconds: session.duration_seconds,
        user_level: session.user_level,
      }));
      
      // Transform generated images to session format
      const imageSessions: SessionUI[] = imagesResponse.data.map(img => ({
        id: `img-${img.id}`,
        type: 'image' as const,
        messageCount: 1,
        title: 'Generated Image',
        preview: img.prompt,
        summary: `Generated: ${img.prompt.substring(0, 50)}...`,
        started_at: img.created_at,
        imageData: img.image_base64,
        mimeType: img.mime_type,
      }));
      
      // Merge and sort by date (newest first)
      const allSessions = [...conversationSessions, ...imageSessions].sort((a, b) => 
        new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
      );
      
      setSessions(allSessions);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const filteredSessions = sessions.filter(session => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = (session.title?.toLowerCase() || '').includes(searchLower) ||
                          (session.summary?.toLowerCase() || '').includes(searchLower) ||
                          session.preview.toLowerCase().includes(searchLower);
    const matchesType = filterType === 'all' || session.type === filterType;
    return matchesSearch && matchesType;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleDelete = async (id: string) => {
    // For now, just remove from local state
    // TODO: Add delete API endpoint
    setSessions(prev => prev.filter(s => s.id !== id));
    if (selectedSession?.id === id) {
      setSelectedSession(null);
    }
  };

  const handleContinue = (session: SessionUI) => {
    // Navigate to assistant with session context
    window.location.href = `/assistant?session=${session.id}`;
  };

  return (
    <AppShell>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Header */}
        <motion.div variants={staggerItem} className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold text-white">
              Session History
            </h1>
            <p className="text-gray-400 mt-1">
              View and continue your previous conversations
            </p>
          </div>
          <button
            onClick={fetchSessions}
            disabled={isLoading}
            className={cn(
              'p-3 rounded-xl transition-all',
              'bg-white/5 border border-white/10 hover:border-ocean-cyan/30',
              'text-white/60 hover:text-ocean-cyan',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            title="Refresh"
          >
            <RefreshCw className={cn('w-5 h-5', isLoading && 'animate-spin')} />
          </button>
        </motion.div>

        {/* Search and Filter */}
        <motion.div variants={staggerItem} className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'w-full pl-12 pr-4 py-3 rounded-xl',
                'bg-white/5 border border-white/10',
                'text-white placeholder-white/40',
                'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/50'
              )}
            />
          </div>

          {/* Type Filter */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'voice', 'document', 'image', 'calculator'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={cn(
                  'px-4 py-3 rounded-xl text-sm font-medium transition-all',
                  filterType === type
                    ? 'bg-ocean-cyan/20 text-ocean-cyan border border-ocean-cyan/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/20'
                )}
              >
                {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <motion.div variants={staggerItem}>
            <GlassPanel className="p-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-ocean-cyan animate-spin mb-4" />
              <p className="text-white/50">Loading sessions...</p>
            </GlassPanel>
          </motion.div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <motion.div variants={staggerItem}>
            <GlassPanel className="p-8 text-center border-red-500/30">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchSessions}
                className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                Try Again
              </button>
            </GlassPanel>
          </motion.div>
        )}

        {/* Sessions List */}
        {!isLoading && !error && (
          <motion.div variants={staggerItem} className="grid gap-4">
            {filteredSessions.length === 0 ? (
              <GlassPanel className="p-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No sessions found</h3>
                <p className="text-white/50 text-sm">
                  {searchQuery || filterType !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Start a new conversation in the Assistant to see it here'}
                </p>
              </GlassPanel>
            ) : (
              filteredSessions.map((session) => {
                const Icon = typeIcons[session.type];
                const isImageSession = session.type === 'image' && session.imageData;
                
                return (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group"
                  >
                    <GlassPanel 
                      className="p-4 cursor-pointer hover:border-ocean-cyan/30 transition-all"
                      animate={false}
                      onClick={() => setSelectedSession(session)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Type Icon or Image Thumbnail */}
                        {isImageSession ? (
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/5">
                            <img 
                              src={`data:${session.mimeType};base64,${session.imageData}`}
                              alt={session.preview}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className={cn(
                            'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                            typeColors[session.type]
                          )}>
                            <Icon className="w-5 h-5" />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-medium text-white group-hover:text-ocean-cyan transition-colors">
                                {session.title || `Session ${session.id.slice(0, 8)}`}
                              </h3>
                              <p className="text-sm text-white/50 line-clamp-2 mt-1">
                                {session.summary || session.preview}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-ocean-cyan transition-colors flex-shrink-0" />
                          </div>

                          {/* Meta */}
                          <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
                            {session.duration_seconds && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(session.duration_seconds)}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(session.started_at)}
                            </span>
                            {session.user_level && (
                              <span className="px-2 py-0.5 rounded-full bg-ocean-cyan/20 text-ocean-cyan text-[10px] uppercase">
                                {session.user_level}
                              </span>
                            )}
                            {isImageSession && (
                              <span className="px-2 py-0.5 rounded-full bg-ocean-pink/20 text-ocean-pink text-[10px] uppercase">
                                AI Generated
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!isImageSession && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContinue(session);
                              }}
                              className="p-2 rounded-lg bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
                              title="Continue"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(session.id);
                            }}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </GlassPanel>
                  </motion.div>
                );
              })
            )}
          </motion.div>
        )}

        {/* Stats */}
        <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GlassPanel className="p-4 text-center" animate={false}>
            <p className="text-2xl font-display font-bold text-white">{sessions.length}</p>
            <p className="text-xs text-white/50">Total Sessions</p>
          </GlassPanel>
          <GlassPanel className="p-4 text-center" animate={false}>
            <p className="text-2xl font-bold text-ocean-cyan">
              {sessions.filter(s => s.type === 'voice').length}
            </p>
            <p className="text-xs text-white/50">Voice Chats</p>
          </GlassPanel>
          <GlassPanel className="p-4 text-center" animate={false}>
            <p className="text-2xl font-bold text-ocean-pink">
              {sessions.filter(s => s.type === 'image').length}
            </p>
            <p className="text-xs text-white/50">Images</p>
          </GlassPanel>
          <GlassPanel className="p-4 text-center" animate={false}>
            <p className="text-2xl font-bold text-ocean-purple">
              {sessions.filter(s => s.type === 'document').length}
            </p>
            <p className="text-xs text-white/50">Documents</p>
          </GlassPanel>
        </motion.div>
      </motion.div>

      {/* Session Detail Modal (BUG-004 fix) */}
      <AnimatePresence>
        {selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-3xl max-h-[80vh] overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/95 to-slate-800/95 border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  {selectedSession.type === 'image' && selectedSession.imageData ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden">
                      <img
                        src={`data:${selectedSession.mimeType};base64,${selectedSession.imageData}`}
                        alt="Generated"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', typeColors[selectedSession.type])}>
                      {(() => {
                        const Icon = typeIcons[selectedSession.type];
                        return <Icon className="w-5 h-5" />;
                      })()}
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {selectedSession.title || `Session ${selectedSession.id.slice(0, 8)}`}
                    </h2>
                    <p className="text-xs text-white/50">
                      {formatDate(selectedSession.started_at)}
                      {selectedSession.duration_seconds && ` • ${formatDuration(selectedSession.duration_seconds)}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
                {/* Image Session */}
                {selectedSession.type === 'image' && selectedSession.imageData && (
                  <div className="space-y-4">
                    <div className="rounded-xl overflow-hidden border border-white/10">
                      <img
                        src={`data:${selectedSession.mimeType};base64,${selectedSession.imageData}`}
                        alt={selectedSession.preview}
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-sm text-white/70 font-medium mb-1">Prompt</p>
                      <p className="text-white">{selectedSession.preview}</p>
                    </div>
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `data:${selectedSession.mimeType};base64,${selectedSession.imageData}`;
                        link.download = `dryvox-image-${selectedSession.id}.${selectedSession.mimeType?.split('/')[1] || 'png'}`;
                        link.click();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
                    >
                      <Download className="w-5 h-5" />
                      Download Image
                    </button>
                  </div>
                )}

                {/* Conversation Session */}
                {selectedSession.type !== 'image' && (
                  <>
                    {isLoadingDetail && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-ocean-cyan animate-spin mb-4" />
                        <p className="text-white/50">Loading conversation...</p>
                      </div>
                    )}

                    {detailError && (
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-center">
                        <p className="text-red-400">{detailError}</p>
                      </div>
                    )}

                    {sessionDetail && sessionDetail.messages && sessionDetail.messages.length > 0 && (
                      <div className="space-y-4">
                        {sessionDetail.messages.map((message, index) => (
                          <div
                            key={message.id || index}
                            className={cn(
                              'flex gap-3',
                              message.role === 'user' ? 'flex-row-reverse' : ''
                            )}
                          >
                            <div
                              className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                                message.role === 'user'
                                  ? 'bg-ocean-cyan/20 text-ocean-cyan'
                                  : 'bg-ocean-purple/20 text-ocean-purple'
                              )}
                            >
                              {message.role === 'user' ? (
                                <User className="w-4 h-4" />
                              ) : (
                                <Bot className="w-4 h-4" />
                              )}
                            </div>
                            <div
                              className={cn(
                                'max-w-[80%] p-3 rounded-2xl',
                                message.role === 'user'
                                  ? 'bg-ocean-cyan/20 text-white rounded-tr-sm'
                                  : 'bg-white/5 text-white/90 rounded-tl-sm'
                              )}
                            >
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                              <p className="text-xs text-white/40 mt-1">
                                {new Date(message.timestamp).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {sessionDetail && (!sessionDetail.messages || sessionDetail.messages.length === 0) && (
                      <div className="text-center py-12">
                        <MessageSquare className="w-12 h-12 mx-auto text-white/20 mb-4" />
                        <p className="text-white/50">No messages in this session</p>
                        {selectedSession.summary && (
                          <p className="text-sm text-white/40 mt-2 max-w-md mx-auto">
                            Summary: {selectedSession.summary}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10">
                {selectedSession.type !== 'image' && !selectedSession.id.startsWith('img-') && (
                  <button
                    onClick={() => handleContinue(selectedSession)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-ocean-cyan/20 text-ocean-cyan hover:bg-ocean-cyan/30 transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Continue Session
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className="px-4 py-2 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppShell>
  );
}

export default Sessions;
